import { memo } from "react";
import { Box, Grid, Typography, Divider } from "@mui/material";
import { useNavigate } from "../router.js";
import theme from "../theme.js";
import panoramaImage from "../assets/images/cycloptPanorama.png";
import companionImage from "../assets/images/cycloptCompanion.png";
import servicesImage from "../assets/images/cycloptServices.png";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { ThirdBackgroundButton, PrimaryBorderButton } from "#components";



// Has an icon and one category that is offered
export const OfferBox = ({ title, CirleOutlineComponent, cirleOutlineColor, titleSx, circleMarginLeft }) => (
	<Box display="flex" alignItems="center">
		<CirleOutlineComponent sx={{ color: cirleOutlineColor, fontSize: "large",  ...(circleMarginLeft && { marginLeft: circleMarginLeft }) }} />
		<Typography sx={{ ...titleSx, marginLeft: "0.5rem" }}>
			{title}
		</Typography>
	</Box>
);

// Has an icon, the offered category and explaination of it
export const OfferVariantBox = ({ title, subtitle, CirleOutlineComponent, circleMarginLeft }) => (
	<Box display="flex" alignItems="flex-start">
		<CirleOutlineComponent sx={{ color: theme.palette.pink.main, fontSize: "large",...(circleMarginLeft && { marginLeft: circleMarginLeft }) }} />
		<Box marginLeft={"0.5rem"}>
			<Typography sx={{ display:"flex", alignItems:"flex-start", fontWeight: "bold" }}>
				{title}
			</Typography>
			<Typography variant="body2" sx={{ color: theme.palette.grey.silver }}>
				{subtitle}
			</Typography>
		</Box>
	</Box>
);

const CompanionBox = () => (
	<>
		<Box
			component="img"
			src={companionImage}
			alt="Companion"
			sx={{
				display: "flex",
				marginBottom: "10px",
				width: "10rem",
			}}
		/>
		<OfferVariantBox title="Source Code Analysis" subtitle="Maintainability + Security + Performance" CirleOutlineComponent={CheckCircleOutlineIcon}/>
		<OfferBox title="Quality Gates" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }}/>
		<OfferBox title="Cyclopt Notifications" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }} />
		<OfferBox title="Cyclopt Profile" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }} />
		<OfferBox title="Cyclopt API" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }} />
		<OfferBox title="Cyclopt Plugins" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }} />
		<OfferBox title="Cyclopt MCP Server" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }} />
		<Box display="flex" alignItems="center">
			<OfferBox title="Automated Code Fixing" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }} />
			<Typography sx={{ color: theme.palette.red.main, marginLeft: "0.2rem" }}>**</Typography>
		</Box>
	</>
);

const PanoramaBox = () => (
	<>
		<Box
			component="img"
			src={panoramaImage}
			alt="Panorama"
			sx={{
				display: "flex",
				marginBottom: "10px",
				width: "10rem",
			}}
		/>
		<OfferVariantBox title="Automated Code Analysis" subtitle="Maintainability + Security + Performance" CirleOutlineComponent={CheckCircleOutlineIcon}/>
		<OfferBox title="Quality Monitoring" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }} />
		<OfferBox title="Early Alerting" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }}/>
		<OfferBox title="Quality Gates" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }}/>
		<OfferBox title="Team Analytics" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }}/>
	</>
	
);

const ServicesBox = () => (
	<>
		<Box
			component="img"
			src={servicesImage}
			alt="Cyclopt Services"
			sx={{
				display: "flex",
				marginBottom: "10px",
				width: "8rem",
			}}
		/>
		<OfferBox title="NIS2 Compliance Reports" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }}/>
		<OfferBox title="Technical Due Diligence" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }}/>
		<OfferBox title="Non-Maintainability Reports" CirleOutlineComponent={CheckCircleOutlineIcon} cirleOutlineColor={theme.palette.pink.main} titleSx={{ fontWeight: "bold" }}/>
	</>
	
);

const PricingBox = ({ price, tdd }) => (
	<>
		<Typography variant="body2" sx={{ color: theme.palette.grey.silver }}>
			{"Starts at:"}
		</Typography>
		<Box display="flex" alignItems="baseline">
			<Typography variant="h5" sx={{ color: "#000", fontWeight: 700 }}>{`€ ${price}`}</Typography>
			<Typography variant="body2" sx={{ color: theme.palette.grey.silver, marginLeft: "0.2rem" }}>{tdd ? "per report" : "per month"}</Typography>
			{price > 0 && <Typography sx={{ color: theme.palette.red.main, marginLeft: "0.2rem" }}>*</Typography>}
		</Box>
	</>
);

const HeaderBox = ({ title, subtitle, backgroundColor, ribbonText = null }) => (
	<Box
		sx={{
			position: "relative", // Needed for absolute positioning of the ribbon
			bgcolor: backgroundColor,
			border: `1px solid ${theme.palette.grey[400]}`,
			borderTopLeftRadius: "1rem",
			borderTopRightRadius: "1rem",
			boxShadow: `0px 1px 1px ${theme.palette.grey[500]}`,
			padding: 2,
			display: "flex",
			flexDirection: "column",
			alignItems: "flex-start",
			overflow: "hidden", // Ensures ribbon does not overflow
		}}
	>
		{/* Ribbon Banner */}
		{ribbonText && (
			<Box
				sx={{
					position: "absolute",
					top: "90px",
					right: "-60px",
					background: "green",
					color: "white",
					padding: "5px 70px",
					fontSize: "12px",
					fontWeight: "bold",
					transform: "rotate(22deg)",
					transformOrigin: "top right",
					boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
					backgroundColor: theme.palette.pink.main,
				}}
			>
				{ribbonText}
			</Box>
		)}
		<Typography sx={{ color: "common.white", fontSize: "x-large", fontWeight: "bold" }}>
			{title}
		</Typography>
		<Typography sx={{ color: "common.white", fontSize: "medium", fontStyle: "italic" }}>
			{subtitle}
		</Typography>
	</Box>
);

const ChoosePlan = () => {
	const navigate = useNavigate();

	return (
		<>
			<div className="background-first-container"></div>
			<div className="background-first-second-container"></div>
			<Grid sx={{ m: 7 }}>
				<Typography sx={{ textAlign: "center", color: theme.palette.primary.main, fontWeight: "bold", fontSize: "2.5rem" }}>
					{"Choose the right DevSecOps Plan for your needs"}
				</Typography>
			</Grid>
			<Grid
				container
				display="flex"
				justifyContent="center"
				alignItems={{ xs:"center", sm: "flex-start" }}
				padding={2}
				sx={{ gap: { xs: 5, sm: 2, md: 15, lg: 20 }, flexDirection: { xs: "column", sm: "row" } }}
			>
				{/* Teams box 1 */}
				<Grid item xs={4} lg={3} minWidth="350px" sx={{ maxWidth: "400px!important" }}>
					<HeaderBox title="Teams" subtitle="For small teams and businesses." backgroundColor={theme.palette.primary.main}/>
					<Grid 
						sx= {{
							border: `1px solid ${theme.palette.grey[400]}`,
							boxShadow: `0px 1px 1px ${theme.palette.grey[500]}`,
							borderBottomLeftRadius: "1rem", borderBottomRightRadius: "1rem",
							backgroundColor: "white"
						}}
					>
						<Grid sx= {{ margin: "1rem" }}>
							<PricingBox price="100" />
							<Divider sx={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}></Divider>
							<PanoramaBox />
							<Divider sx={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}></Divider>
							<CompanionBox />
							<Grid sx={{ display: "flex", justifyContent: "center", marginTop: "1rem", marginBottom: "1rem", flexDirection: "column" }}>
								<ThirdBackgroundButton
									id="configure-teams-plan-button"
									width="100%"
									title="Configure Your Plan"
									onClick={() => navigate("../team-plan")}
								/>
								<Grid mt={1} />
								<PrimaryBorderButton
									id="redeem-team-plan-coupon"
									width="100%"
									title="Redeem Coupon"
									onClick={() => window.location.assign("https://configurator.cyclopt.com/")}
								/>
							</Grid>
							<Box display="flex" alignItems="center">
								<Typography sx={{ color: theme.palette.red.main }}>*</Typography>
								<Typography sx={{ marginLeft: "0.2rem", fontSize: "small" }} >+ VAT if applicable</Typography>
							</Box>
							<Box display="flex" alignItems="center">
								<Typography sx={{ color: theme.palette.red.main }}>**</Typography>
								<Typography sx={{ marginLeft: "0.2rem", fontSize: "small" }} > Available as an add-on for an additional fee.</Typography>
							</Box>
						</Grid>
					</Grid>
				</Grid>
				{/* Solo box 2 */}
				<Grid item xs={4} lg={3} minWidth="350px" sx={{ maxWidth: "400px!important" }}>
					<HeaderBox title="Solo" subtitle="For individual developers." backgroundColor={theme.palette.primary.main} ribbonText="Free for 30 commits per month" />
					<Grid 
						sx= {{
							border: `1px solid ${theme.palette.grey[400]}`,
							boxShadow: `0px 1px 1px ${theme.palette.grey[500]}`,
							borderBottomLeftRadius: "1rem", borderBottomRightRadius: "1rem",
							backgroundColor: "white"
						}}
					>
						<Grid sx= {{ margin: "1rem" }}>
							<PricingBox price="0" />
							<Divider sx={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}></Divider>
							<CompanionBox />
							<Grid sx={{ display: "flex", justifyContent: "center", marginTop: "1rem", marginBottom: "1rem", flexDirection: "column" }}>
								<ThirdBackgroundButton
									id="configure-solo-plan-button"
									width="100%"
									title="Configure Your Plan"
									onClick={() => navigate("../solo-plan")}
								/>
								<Grid mt={1} />
								<PrimaryBorderButton
									id="redeem-solo-plan-coupon"
									width="100%"
									title="Redeem Coupon"
									onClick={() => window.location.assign("https://configurator.cyclopt.com/")}
								/>
							</Grid>
							<Box display="flex" alignItems="center">
								<Typography sx={{ color: theme.palette.red.main }}>*</Typography>
								<Typography sx={{ marginLeft: "0.2rem", fontSize: "small" }} >+ VAT if applicable</Typography>
							</Box>
							<Box display="flex" alignItems="center">
								<Typography sx={{ color: theme.palette.red.main }}>**</Typography>
								<Typography sx={{ marginLeft: "0.2rem", fontSize: "small" }} > Available as an add-on for an additional fee.</Typography>
							</Box>
						</Grid>
					</Grid>
				</Grid>
				{/* TDD */}
				<Grid item minWidth="350px" sx={{ width: "40%", maxWidth: "400px!important" }}>
					<HeaderBox title="Services" subtitle="Various tools & services" backgroundColor={theme.palette.primary.main} />
					<Box
						sx={{
							border: `1px solid ${theme.palette.grey[400]}`,
							boxShadow: `0px 1px 1px ${theme.palette.grey[500]}`,
							borderBottomLeftRadius: "1rem",
							borderBottomRightRadius: "1rem",
							backgroundColor: "white",
						}}
					>
						<Box sx={{ margin: "1rem" }}>
							{/* <PricingBox tdd price="500" /> */}
							{/* <Divider sx={{ marginTop: "0.5rem", marginBottom: "0.5rem" }} /> */}
							<ServicesBox tdd />
							<Grid sx={{ display: "flex", justifyContent: "center", marginTop: "1rem", marginBottom: "1rem", flexDirection: "column" }}>
								<ThirdBackgroundButton
									id="configure-teams-plan-button"
									width="100%"
									title="Configure Your Plan"
									onClick={() => navigate("../services")}
								/>
								<Grid mt={1} />
								<PrimaryBorderButton
									id="redeem-services-coupon"
									width="100%"
									title="Redeem Coupon"
									onClick={() => window.location.assign("https://configurator.cyclopt.com")}
								/>
							</Grid>
							<Box display="flex" alignItems="center">
								<Typography sx={{ color: theme.palette.red.main }}>*</Typography>
								<Typography sx={{ marginLeft: "0.2rem", fontSize: "small" }} >+ VAT if applicable</Typography>
							</Box>
						</Box>
					</Box>
				</Grid>
			</Grid>
		</>
	
	
	);
};

export default memo(ChoosePlan);
